<template>
  <div
    v-if="curStepData"
    :style="`--stepColor: #${curStepData.color}`"
    class="category-wizard-mobile"
  >
    <CategoryColorBar
      :active-step="currentStep"
      class="category-wizard-mobile__bar"
    />
    <div class="safe-horizontal-container">
      <div class="category-wizard-mobile__category-name-wrapper">
        <span class="category-wizard-mobile__category-name">
          {{ curStepData.label }}
        </span>
        <ElTag
          v-if="showLevelTag"
          :size="$screen.smDown ? 'small' : 'default'"
          class="category-wizard-mobile__category-level"
          data-tag-type="grey"
          effect="plain"
        >
          {{ $t("common.levelTag", { level: selectedStepData.level }) }}
        </ElTag>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { IStepItem } from "@/modules/self-assessment/types/step-item.interface";
import { IViralLevelState } from "@/services/store/viral-level/viral-level-types";
import { ICategoryLevelSelection } from "@/services/data/viral-level/viral-level.interface";
import { ICategory } from "@/services/data/category/category.interface";

import CategoryColorBar from "@/modules/self-assessment/components/category-color-bar/category-color-bar.vue";
import { ILevel } from "@/services/data/level/level.interface";

export default defineComponent({
  name: "CategoryWizardMobile",

  components: {
    CategoryColorBar,
  },

  props: {
    steps: {
      type: Array as () => any[],
      required: true,
    },
    currentStep: {
      type: Number,
      default: 0,
    },
    active: {
      type: Number,
      default: null,
    },
  },

  computed: {
    /**
     * Viral level state.
     */
    viralLevelState(): IViralLevelState {
      return this.$store.state.viralLevel;
    },

    levels(): Array<ICategoryLevelSelection> {
      return this.viralLevelState.levels;
    },

    /**
     * Get all categories from the store.
     */
    categories(): Array<ICategory> {
      return this.$store.state.categories.data;
    },

    /**
     * Information of the current selected step.
     */
    curStepData(): IStepItem {
      return this.steps[this.currentStep];
    },

    /**
     * Get the category for the current step.
     */
    currentCategory(): ICategory | null {
      if (this.categories.length === 0) {
        return null;
      }

      return this.categories[this.currentStep];
    },

    /**
     * Get the data for the selected step.
     */
    selectedStepData(): ICategoryLevelSelection | null {
      if (!this.levels || this.levels.length === 0) {
        return null;
      }

      const selectedDetail = this.levels.find(
        (record: ICategoryLevelSelection): any => {
          return (
            this.currentCategory && record.category === this.currentCategory.id
          );
        },
      );

      return selectedDetail || null;
    },

    /**
     * Informs if the level tag must be shown.
     *
     * The tag only is visible when there is a level selected.
     */
    showLevelTag(): boolean {
      return (
        !!this.selectedStepData &&
        (this.selectedStepData.level as ILevel["value"]) > 0
      );
    },
  },
});
</script>

<style lang="scss" scoped>
.category-wizard-mobile {
  position: relative;
  display: block;

  background: white;

  &::after {
    position: relative;
    display: block;
    width: 100%;
    height: 4px;

    content: "";

    background-color: var(--stepColor);
  }

  &__category-name-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 15px 16px;
  }

  &__category-name {
    @include grotesk(semiBold);

    font-size: 1.4667rem;
    line-height: 1;
  }

  &__category-level {
    position: absolute;
    right: 15px;

    background-color: var(--stepColor);
    border: solid 1px rgba(0, 0, 0, 0.06);
  }
}

.category-wizard-mobile__bar {
  padding-top: 2px;
}
</style>
